export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{1,3})(\d{3})(\d{4})$/);
  if (match) {
    return `+${match[1]} ${match[2]} ${match[3]}`;
  }
  return phone;
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

export const formatStatus = (status) => {
  const statusMap = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    no_show: 'No Show'
  };
  return statusMap[status] || status;
};

const normalizeDateStr = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') {
    try {
      return new Date(value).toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  }
  return value.includes('T') ? value.split('T')[0] : value;
};

export const resolveImageUrl = (value) => {
  if (!value || typeof value !== 'string') return '';

  const v = value.trim();
  if (!v) return '';

  if (v.startsWith('http://') || v.startsWith('https://') || v.startsWith('data:') || v.startsWith('blob:')) {
    return v;
  }

  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) return v;

  try {
    const parsed = new URL(apiUrl);
    const origin = parsed.origin;
    const needsApiPrefix = parsed.pathname.includes('/api/api/');
    const prefix = needsApiPrefix ? '/api' : '';
    const normalized = v.startsWith('/') ? v : `/${v}`;
    return `${origin}${prefix}${normalized}`;
  } catch (e) {
    return v;
  }
};

export const formatNotificationMessage = (notification) => {
  if (!notification) return '';

  const rawMeta = notification.metadata;
  let metadata = null;
  if (rawMeta) {
    if (typeof rawMeta === 'string') {
      try {
        metadata = JSON.parse(rawMeta);
      } catch (e) {
        metadata = null;
      }
    } else if (typeof rawMeta === 'object') {
      metadata = rawMeta;
    }
  }

  const type = metadata?.notificationType;
  if (!type) return notification.message || '';
  if (metadata?.timezone_applied) return notification.message || '';

  const bookingDate = normalizeDateStr(
    metadata.booking_date || metadata.new_date || metadata.old_date
  );

  const toLocalTime = (t) => {
    if (!t || !bookingDate) return t || '';
    try {
      const [y, mo, da] = bookingDate.split('-').map(Number);
      const parts = String(t).split(':').map(Number);
      const h = parts[0] ?? 0;
      const m = parts[1] ?? 0;
      const s = parts[2] ?? 0;
      const dt = new Date(Date.UTC(y, mo - 1, da, h, m, s));
      return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(dt);
    } catch (e) {
      return t;
    }
  };

  const toLocalRange = (start, end) => {
    const s = toLocalTime(start);
    const e = toLocalTime(end);
    if (!s && !e) return '';
    if (!e) return s;
    if (!s) return e;
    return `${s} - ${e}`;
  };

  switch (type) {
    case 'booking_confirmation': {
      const time = metadata.end_time ? toLocalRange(metadata.start_time, metadata.end_time) : toLocalTime(metadata.start_time);
      return `Your booking for ${metadata.service_name} on ${bookingDate} at ${time} has been confirmed.`;
    }
    case 'booking_received_owner': {
      const time = metadata.end_time ? toLocalRange(metadata.start_time, metadata.end_time) : toLocalTime(metadata.start_time);
      return `New booking: ${metadata.service_name} on ${bookingDate} at ${time} (${metadata.customer_name || 'Customer'}).`;
    }
    case 'booking_reminder': {
      const time = toLocalTime(metadata.start_time);
      return `Reminder: ${metadata.service_name} tomorrow at ${time}.`;
    }
    case 'booking_cancelled': {
      return `Your booking for ${metadata.service_name} on ${bookingDate} has been cancelled.`;
    }
    case 'booking_rescheduled': {
      const time = toLocalTime(metadata.new_start_time);
      const date = normalizeDateStr(metadata.new_date);
      return `Your booking has been rescheduled to ${date} at ${time}.`;
    }
    default:
      return notification.message || '';
  }
};
